import {
  ResponseResult,
  type QuestionResponseDetails,
  type SubQuestionResponseEntry,
  type SubResponse,
  type UpdateAuditQuestionResponseCommandInput,
} from '@amzn/gbsa-helios-typescript-client';
import { scoredSubQuestionIds, type SubQuestionTypeRef } from './deriveParentResult';
import { deriveSubResponsesStatus } from './deriveSubResponsesStatus';
import {
  buildPreservedResponseData,
  projectSubResponsesForPreservation,
  type NaReasonOverride,
} from './preserveResponseFields';

/**
 * Arguments for {@link buildBulkAnswerUpdate}. Bundled in a single
 * object so future fields (per-child comment / naReason overrides,
 * `expectedLastUpdatedAt`) slot in without shifting args.
 */
export interface BuildBulkAnswerUpdateArgs {
  readonly businessUnitId: string;
  readonly auditId: string;
  readonly questionId: string;
  /** Current user alias — recorded as `respondedBy` / `lastUpdatedBy`
   *  on the outgoing payload. Stamped identically on every child. */
  readonly alias: string;
  /** Pass / Fail / N/A applied uniformly to every SCORED sub-question. */
  readonly result: ResponseResult;
  /**
   * Id + ERT responseType of every sub-question defined on the
   * parent. The bulk builder stamps the tapped result onto the
   * SCORED (Pass/Fail) subset only — data-collection children
   * capture values, not results, and ride through preserved.
   * Consumers pass every def on the parent; omitting one would
   * leave that child's server-side answer untouched, which defeats
   * "Pass All" semantics.
   */
  readonly subQuestionRefs: readonly SubQuestionTypeRef[];
  /**
   * Existing sub-question responses on the parent (from the summary
   * cache). Projected onto the wire so non-result fields — comments,
   * media, tasks, AND data-collection responseType/responseValue —
   * survive the full-PATCH. Undefined / empty is fine.
   */
  readonly subQuestionResponses?: readonly SubQuestionResponseEntry[] | undefined;
  /**
   * The parent question's cached response, or undefined when the
   * parent has never been answered. Preservation is the same as
   * {@link buildAnswerUpdate}: comment / naReason / mediaIds /
   * taskIds survive the round-trip via the shared
   * {@link buildPreservedResponseData} (naReason cleared when the
   * bulk result isn't NA).
   */
  readonly previous?: QuestionResponseDetails | undefined;
}

/**
 * Builds the mutation input for a BULK Pass All / Fail All / N/A All
 * save. Every SCORED sub-question on the parent receives the same
 * tapped result; the parent's aggregate result is set to that same
 * value (uniform scored children → parent's derivation collapses to
 * the shared result, which we compute client-side rather than
 * round-tripping server derivation). Data-collection children are
 * NOT stamped — they are merged through the preservation projection
 * so their captured values survive the full-PATCH.
 *
 * Contract vs the two sibling builders:
 *
 *   - vs {@link buildAnswerUpdate} (parent-flat path): both set the
 *     top-level `responseResult`. Difference is `subResponses` —
 *     parent-flat preserves existing sub-answers verbatim, bulk
 *     overwrites every scored child's RESULT while preserving all
 *     other per-child fields.
 *   - vs {@link buildSubQuestionAnswerUpdate} (per-row path): bulk
 *     sets top-level `responseResult` because the scored children
 *     ARE uniform (client-side certainty); per-row leaves it unset
 *     because the server has to derive from a mix.
 *
 * `responseStatus` comes from the shared
 * {@link deriveSubResponsesStatus} — a mixed parent whose
 * data-collection children still lack values stays PENDING even
 * though every scored child just got the bulk result.
 *
 * Pure — no react, no side effects. Consumed by the bulk draft
 * hook and by the bulk optimistic cache patch (both must write the
 * same shape so wire and cache stay coherent).
 */
export function buildBulkAnswerUpdate(
  args: BuildBulkAnswerUpdateArgs,
): UpdateAuditQuestionResponseCommandInput {
  const scoredIds = scoredSubQuestionIds(args.subQuestionRefs);
  const clearNaReasons = args.result !== ResponseResult.NA;

  // Clear every SCORED child's naReason on the wire when the bulk
  // result isn't NA — a previously-picked reason must not survive a
  // Pass All / Fail All answer change. Data-collection children are
  // never NA, so their (nonexistent) reasons are left alone.
  // Object.fromEntries over per-key assignment sidesteps the
  // security/detect-object-injection sink.
  const naReasonOverrides: Record<string, NaReasonOverride> = clearNaReasons
    ? Object.fromEntries(scoredIds.map((id) => [id, null]))
    : {};
  const projected = projectSubResponsesForPreservation(args.subQuestionResponses, {
    naReasonOverrides,
  });
  const subResponses = mergeBulkResult(
    projected,
    args.subQuestionRefs,
    scoredIds,
    args.result,
  );

  const input: UpdateAuditQuestionResponseCommandInput = {
    businessUnitId: args.businessUnitId,
    auditId: args.auditId,
    questionId: args.questionId,
    responseStatus: deriveSubResponsesStatus(subResponses, args.subQuestionRefs.length),
    responseResult: args.result,
    respondedBy: args.alias,
    lastUpdatedBy: args.alias,
    subResponses,
  };

  const preservedResponse = buildPreservedResponseData(
    args.previous,
    clearNaReasons ? { naReasonOverride: null } : {},
  );
  if (preservedResponse) {
    input.response = preservedResponse;
  }

  return input;
}

/**
 * Stamp the bulk result onto every scored child, emitting entries in
 * DEFINITION order (mirrors the bulk cache patch so wire and cache
 * stay coherent): scored children have their `result` replaced over
 * the projected entry (all other projected fields intact) or get a
 * fresh `{ subQuestionId, result }` when never answered; projected
 * data-collection entries ride through untouched at their definition
 * position; orphan projected entries (no matching def) keep their
 * relative order at the end.
 */
function mergeBulkResult(
  projected: readonly SubResponse[],
  subQuestionRefs: readonly SubQuestionTypeRef[],
  scoredIds: readonly string[],
  result: ResponseResult,
): SubResponse[] {
  const projectedById = new Map(projected.map((entry) => [entry.subQuestionId, entry]));
  const scoredIdSet = new Set(scoredIds);
  const merged: SubResponse[] = [];
  const consumedIds = new Set<string>();
  for (const ref of subQuestionRefs) {
    if (!ref.subQuestionId) continue;
    consumedIds.add(ref.subQuestionId);
    const entry = projectedById.get(ref.subQuestionId);
    if (scoredIdSet.has(ref.subQuestionId)) {
      merged.push(
        entry ? { ...entry, result } : { subQuestionId: ref.subQuestionId, result },
      );
    } else if (entry) {
      merged.push(entry);
    }
  }
  for (const entry of projected) {
    if (!consumedIds.has(entry.subQuestionId)) merged.push(entry);
  }
  return merged;
}
